import { useEffect, useState, useRef, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import PdfExportButton from "../components/PdfExportButton";
import { toast } from "sonner";
import TradeInPotentialSection from "../components/TradeInPotentialSection";
import { Download, RefreshCw, LogOut, Users, CheckCircle2, XCircle, Clock, Trophy, TrendingUp, AlertTriangle, ArrowUpDown, ChevronDown, ChevronUp, Info } from "lucide-react";
import MultiSelect from "../components/MultiSelect";
import { RefetchPill } from "../components/Loader";
import useMinVisible from "../hooks/useMinVisible";
import { AIInsightsPanel, AIChatWidget, UnmanagedPaginated } from "../components/AIPanels";
import { LeadJourneyLauncher } from "../components/LeadJourneyModal";

const MONTHS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const STATUS_KEYS = ["Calificado","Abierto","Descalificado"];
const PIPELINE_STAGES = ["Nuevo","No Contactado","Intentando Contactar","Contactado","Calificado"];
const AGE_BUCKETS = ["18-24","25-34","35-44","45-54","55+","Desconocido"];

const fmt = (n) => Number(n || 0).toLocaleString("es-DO");
const pct = (a, b) => (b ? ((a / b) * 100).toFixed(1) + "%" : "0.0%");
const shortK = (n) => (n >= 1000 ? (n / 1000).toFixed(1).replace(".0", "") + "k" : String(n));

// --- Tooltip definitions for every indicator ---
const TIPS = {
  total: <>Conteo total de leads cuyo <strong>campo Estado</strong> existe en la base, dentro de los filtros activos. Equivale a <code>count(lead_id)</code>.</>,
  qual_rate: <>Leads con <strong>Estado = Calificado</strong> dividido entre total de leads filtrados. <code>Calificados / Total × 100</code>.</>,
  disq_rate: <>Leads con <strong>Estado = Descalificado</strong> dividido entre total filtrado. <code>Descalificados / Total × 100</code>. Sumar este % + Calificado % + Abierto % da 100.</>,
  response: <>Promedio de horas entre <strong>Fecha de creación</strong> y <strong>Fecha de modificación</strong> del lead. Se ignoran valores fuera de [0, 720h] para limpiar outliers. Si supera 48h se muestra en días.</>,
  top_origin: <>Canal de adquisición con mayor volumen de leads (campo <strong>Origen de cliente potencial</strong>). El % al lado es su tasa de calificación.</>,
  pipeline: <>Conteos por <strong>Razón para el estado</strong>: Nuevo, No Contactado, Intentando Contactar, Contactado, Calificado. Son las 5 etapas del proceso comercial extraídas directo del CRM.</>,
  funnel: <>Pipeline en 3 niveles: Total filtrado → Contactados (Razón ∈ {"{Contactado, Calificado}"}) → Calificados (Estado=Calificado). El % de cada paso es vs el Total inicial.</>,
  reasons: <>Top 10 valores del campo <strong>Razón para el estado</strong>. Colores: verde=positivo (Calificado, Contactado), ámbar=sin gestionar, rojo=perdido (Sin Interés, Sin Presupuesto, Duplicado, Data Errónea).</>,
  walkin: <>Clasificación por <strong>Vía de primer contacto</strong>. Digital = {"{Teléfono, Redes Sociales, WhatsApp, Chat, Email}"}. Walk-in = {"{Walk In, Visita al cliente}"}. Otros = referidos/prospección.</>,
  brand_perf: <>Mix porcentual de cada marca: Calificado / Abierto / Descalificado. Ordenado por volumen descendente, top 10.</>,
  exec_ranking: <>Top 10 ejecutivos por <strong>tasa de calificación</strong> (≥50 leads para evitar ruido). El tiempo de respuesta es el promedio del ejecutivo. Medallas 🥇🥈🥉 para top 3.</>,
  models: <>Top 10 valores del campo <strong>Modelo de Interés</strong> en los leads filtrados.</>,
  campaigns: <>Top 10 campañas por volumen. El % verde al lado es la tasa de calificación de esa campaña.</>,
  timeline: <>Heatmap de las 10 campañas top × 12 meses. <strong>Volumen</strong>: intensidad amarilla proporcional al máximo de leads en cualquier celda. <strong>Calidad %</strong>: tasa de calificación calculada como <em>leads calificados ÷ leads totales</em> para esa campaña en ese mes. Escala: rojo (&lt;3%), naranja (3-10%), amarillo (10-20%), verde (20-30%), verde fuerte (≥30%).</>,
  age: <>Calculado de <strong>Cumpleaños</strong> del contacto principal vs fecha de hoy. Sin cumpleaños → <code>Desconocido</code>. Buckets: 18-24, 25-34, 35-44, 45-54, 55+.</>,
  branches: <>Volumen y % calificación por <strong>Unidad de Negocio propietaria</strong>. Las Unidades de Negocio tipo <code>orgXXX</code> se renombran a "General / Sin Asignar".</>,
  comparison: <>Compara el período actual contra el mes anterior (MoM) o el año anterior (YoY). Si filtras un mes específico, MoM compara mes vs mes; si filtras "Todos", compara año vs año.</>,
  unmanaged: <>Leads cuyo <strong>Razón del estado</strong> es Nuevo, No Contactado, o Intentando Contactar. Requieren acción inmediata del equipo.</>,
};

const DEFAULT_FILTERS = {
  year: String(new Date().getFullYear()),
  month: "Todos",
  brand: "Todas",
  branch: "Todas",
  executive: "Todos",
  status: "Todos",
  origin: "Todos",
  contact: "Todos",
  age_bucket: "Todos",
  campaign: "Todas",
  reason: "Todas",
};

export default function DashboardPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const FILTER_KEY = "crm_filters_v2";
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(FILTER_KEY);
      return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : DEFAULT_FILTERS;
    } catch { return DEFAULT_FILTERS; }
  });
  useEffect(() => { try { localStorage.setItem(FILTER_KEY, JSON.stringify(filters)); } catch { /* ignore */ } }, [filters]);
  const [options, setOptions] = useState({
    brands: [], branches: [], executives: [], years: ["2025"], months: MONTHS,
    statuses: STATUS_KEYS, origins: [], contacts: [], campaigns: [], age_buckets: AGE_BUCKETS,
  });
  const [summary, setSummary] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [compareMode, setCompareMode] = useState("MoM"); // MoM | YoY
  const [campaignTimeline, setCampaignTimeline] = useState(null);
  const [timelineMetric, setTimelineMetric] = useState("volume"); // volume | quality
  const [loading, setLoading] = useState(true);
  const showRefetchPill = useMinVisible(loading, 700);
  const [dealersBreakdown, setDealersBreakdown] = useState(null);
  const [brandTab, setBrandTab] = useState("nuevos"); // nuevos | seminuevos
  const [execTab, setExecTab] = useState("ventas"); // ventas | bdc

  const fetchAll = async (f = filters) => {
    setLoading(true);
    try {
      const [opt, s, c, t] = await Promise.all([
        api.get("/dashboard/options", { params: f }),
        api.get("/dashboard/summary", { params: f }),
        api.get("/dashboard/comparison", { params: f }),
        api.get("/dashboard/campaign-timeline", { params: { ...f, top: 10 } }),
      ]);
      setOptions(opt.data);
      setSummary(s.data);
      setComparison(c.data);
      setCampaignTimeline(t.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(filters); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-switch year on first load: if the current year filter has 0 leads but
  // another year has data, jump to the year with the most leads. Runs ONCE per session.
  const autoSwitchedRef = useRef(false);
  useEffect(() => {
    if (autoSwitchedRef.current) return;
    const years = options?.years;
    if (!Array.isArray(years) || years.length === 0) return;
    // Normalise: years can be ["2025"] (initial) or [{value, count}] (from API)
    const normalised = years.map((y) =>
      typeof y === "string" ? { value: y, count: 0 } : y
    );
    if (normalised.every((y) => (y.count || 0) === 0)) return; // no data anywhere yet
    const current = normalised.find((y) => String(y.value) === String(filters.year));
    if (current && (current.count || 0) > 0) {
      autoSwitchedRef.current = true; // current year already has data — nothing to do
      return;
    }
    const best = [...normalised].sort((a, b) => (b.count || 0) - (a.count || 0))[0];
    if (best && String(best.value) !== String(filters.year)) {
      autoSwitchedRef.current = true;
      setFilters((f) => ({ ...f, year: String(best.value) }));
    }
  }, [options, filters.year]);

  // Fetch dealers breakdown — respects current filters so tooltip matches the chart bar
  useEffect(() => {
    api.get("/dashboard/dealers-breakdown", { params: filters }).then((r) => setDealersBreakdown(r.data)).catch(() => {});
  }, [filters]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v, ...(k === "status" ? { reason: "Todas" } : {}) }));
  const toggle = (key, value, defaultVal) => setF(key, filters[key] === value ? defaultVal : value);
  const reset = () => {
    autoSwitchedRef.current = true; // prevent the auto-switch from undoing the reset
    setFilters({ ...DEFAULT_FILTERS, year: "Todos" });
  };

  const [csvBusy, setCsvBusy] = useState(false);
  const onExportCSV = async () => {
    setCsvBusy(true);
    try {
      const res = await api.get("/leads/export-csv", { responseType: "blob", timeout: 180000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = "leads_export.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error("No se pudo descargar el CSV. Intenta de nuevo.");
    } finally {
      setCsvBusy(false);
    }
  };

  if (loading && !summary) {
    return <div className="min-h-screen grid place-items-center"><div className="spinner" style={{ borderColor: "#11182733", borderTopColor: "#111827" }}></div></div>;
  }
  if (!summary || summary.total === 0) {
    // If options has data on OTHER years, offer quick-switch chips so the user
    // is never stuck on an empty dashboard just because of a stale year filter.
    const yearsWithData = (options?.years || [])
      .map((y) => (typeof y === "string" ? { value: y, count: 0 } : y))
      .filter((y) => (y.count || 0) > 0)
      .sort((a, b) => (b.count || 0) - (a.count || 0));

    // Detect whether the user has any non-default filter saved. When
    // `yearsWithData` is empty this almost always means an over-restrictive
    // saved filter (from localStorage) references a value that no longer
    // exists in the data — the user is otherwise stuck with no way to
    // recover from the empty state.
    const hasActiveFilters = Object.keys(DEFAULT_FILTERS).some(
      (k) => String(filters[k] ?? "") !== String(DEFAULT_FILTERS[k] ?? "")
    );

    const hardReset = () => {
      try { localStorage.removeItem(FILTER_KEY); } catch { /* ignore */ }
      autoSwitchedRef.current = false;
      setFilters({ ...DEFAULT_FILTERS, year: "Todos" });
    };

    return (
      <div className="max-w-3xl mx-auto p-10">
        <div className="surface p-10 text-center">
          <h2 className="font-display text-2xl font-extrabold mb-2">Sin datos para este filtro</h2>
          {yearsWithData.length > 0 ? (
            <>
              <p className="text-gray-500 mb-6">
                No hay leads para <strong>{filters.year}</strong> con los filtros actuales.
                Tienes datos en {yearsWithData.length === 1 ? "este año" : "estos años"}:
              </p>
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                {yearsWithData.map((y) => (
                  <button
                    key={y.value}
                    onClick={() => setFilters((f) => ({ ...f, year: String(y.value) }))}
                    className="btn"
                    data-testid={`switch-year-${y.value}`}
                    style={{ background: "#111827", color: "#fff", padding: "8px 16px", borderRadius: 8 }}
                  >
                    Ver {y.value} ({(y.count || 0).toLocaleString()} leads)
                  </button>
                ))}
              </div>
              <button
                onClick={hardReset}
                className="btn"
                data-testid="empty-reset-filters"
                style={{ background: "transparent", color: "#6b7280", border: "1px solid #e5e7eb", padding: "8px 16px", borderRadius: 8 }}
              >
                O restablecer todos los filtros
              </button>
            </>
          ) : hasActiveFilters ? (
            <>
              <p className="text-gray-500 mb-6">
                Tus filtros guardados no coinciden con ningún lead actual (posiblemente por un
                filtro antiguo o valores que ya no existen). Restablece los filtros para ver
                todos los datos.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                <button
                  onClick={hardReset}
                  className="btn primary"
                  data-testid="empty-reset-filters-hard"
                  style={{ padding: "10px 20px", borderRadius: 8 }}
                >
                  Restablecer todos los filtros
                </button>
                <button
                  onClick={async () => { await logout(); navigate("/login"); }}
                  className="btn"
                  data-testid="empty-logout"
                  style={{ background: "transparent", color: "#6b7280", border: "1px solid #e5e7eb", padding: "10px 20px", borderRadius: 8 }}
                >
                  Cerrar sesión
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Si el problema persiste después de restablecer, contacta al administrador.
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-500 mb-6">Sube el CSV o XLSX exportado del CRM desde la sección de Integraciones para comenzar.</p>
              <Link to="/integraciones" className="btn primary inline-block" data-testid="empty-upload-cta">Ir a Integraciones</Link>
            </>
          )}
        </div>
      </div>
    );
  }

  const s = summary;
  const total = s.total;

  // KPI: Tiempo de respuesta — format as days+hours if large
  const respFmt = (h) => {
    if (h == null) return "—";
    if (h < 48) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };

  // Pipeline stages — counts come from reasons
  const reasonMap = Object.fromEntries(s.reasons.map(r => [r.name, r.n]));
  const pipelineCounts = PIPELINE_STAGES.map(st => ({ name: st, value: reasonMap[st] || 0 }));

  // Reasons
  const reasonMax = s.reasons.length ? s.reasons[0].n : 1;

  // Models
  const modelMax = s.models.length ? s.models[0].total : 1;

  // Executive ranking — top 10 by qual_rate, only those with meaningful volume
  const execs = s.executives.filter(e => e.total >= 50).slice(0, 10);
  const execsVentas = (s.executives_ventas || []).filter(e => e.total >= 50).slice(0, 10);
  const execsBdc = (s.executives_bdc || []).filter(e => e.total >= 30).slice(0, 10);
  const execMaxQual = execs.length ? Math.max(...execs.map(e => e.qual_rate), 1) : 1;
  const execMaxQualVentas = execsVentas.length ? Math.max(...execsVentas.map(e => e.qual_rate), 1) : 1;
  const execMaxQualBdc = execsBdc.length ? Math.max(...execsBdc.map(e => e.qual_rate), 1) : 1;
  const medalFor = (i) => i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i+1}`;

  const brandsNuevos = s.brands_nuevos || [];
  const brandsSeminuevos = s.brands_seminuevos || [];
  const currentBrands = brandTab === "nuevos" ? brandsNuevos : brandsSeminuevos;
  const currentExecs = execTab === "ventas" ? execsVentas : execsBdc;
  const currentExecMaxQual = execTab === "ventas" ? execMaxQualVentas : execMaxQualBdc;
  const currentExecMinVol = execTab === "ventas" ? 50 : 30;

  // Active filters chips
  const activeChips = [
    ["Año", filters.year, "Todos", "year"],
    ["Mes", filters.month, "Todos", "month"],
    ["Marca", filters.brand, "Todas", "brand"],
    ["Unidad de Negocio", filters.branch, "Todas", "branch"],
    ["Ejecutivo", filters.executive, "Todos", "executive"],
    ["Estado", filters.status, "Todos", "status"],
    ["Razón", filters.reason, "Todas", "reason"],
    ["Canal", filters.origin, "Todos", "origin"],
    ["Campaña", filters.campaign, "Todas", "campaign"],
    ["Vía", filters.contact, "Todos", "contact"],
  ].filter(([, v, d]) => v !== d);

  return (
    <div ref={pageRef} className="w-full mx-auto p-5" style={{ maxWidth: "min(100%, 1920px)" }}>
      {showRefetchPill && summary && <RefetchPill/>}
      {/* Topbar */}
      <header className="surface flex justify-between items-center gap-4 p-5 mb-5 flex-wrap" data-pdf-hide>
        <div>
          <h1 className="text-2xl font-extrabold font-display">Análisis de Performance CRM</h1>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <PdfExportButton
            targetRef={pageRef}
            filename="dashboard-crm"
            title="SDM · Análisis de Performance CRM"
            subtitle={`${filters.year || ""} ${filters.month || ""}`.trim()}
            testid="dashboard-pdf-btn"
          />
          <button className="btn flex items-center gap-2" onClick={onExportCSV} disabled={csvBusy} data-testid="export-csv-btn">
            {csvBusy ? <RefreshCw size={16} className="animate-spin"/> : <Download size={16}/>} {csvBusy ? "Generando…" : "CSV"}
          </button>
          <button className="btn flex items-center gap-2" onClick={() => fetchAll()} data-testid="refresh-btn"><RefreshCw size={16}/></button>
          <button className="btn primary" onClick={reset} data-testid="reset-filters-btn">Borrar Filtros</button>
          <button className="btn flex items-center gap-2" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn"><LogOut size={16}/></button>
        </div>
      </header>

      {/* Filters — collapsible on mobile via <details>. Always open ≥ 768px. */}
      <details className="lp2-filters-details surface p-4 mb-5"
               open={typeof window !== "undefined" && window.innerWidth >= 768}>
        <summary className="lp2-filters-summary">
          <span className="lp2-filters-summary-label">Filtros</span>
          <ChevronDown size={16} className="lp2-filters-summary-chev"/>
        </summary>
        <div className="sp-filter-grid-9">
          {[
            ["Año",            "year",      options.years,                                "Todos"],
            ["Mes",            "month",     MONTHS.map((m) => ({ value: m })),            "Todos"],
            ["Marca",          "brand",     options.brands,                               "Todas"],
            ["Unidad de Negocio","branch",  options.branches,                             "Todas"],
            ["Ejecutivo",      "executive", (options.executives || []).slice(0, 200),     "Todos"],
            ["Estado",         "status",    options.statuses,                             "Todos"],
            ["Razón para estado","reason",  options.reasons,                              "Todas"],
            ["Canal",          "origin",    options.origins,                              "Todos"],
            ["Campaña",        "campaign",  (options.campaigns || []).slice(0, 300),      "Todas"],
          ].map(([label, key, opts, allLabel]) => (
            <div key={key}>
              <label className="label-up block mb-2">{label}</label>
              <MultiSelect
                value={filters[key]}
                onChange={(v) => setF(key, v)}
                placeholder={label}
                options={opts || []}
                allValue={allLabel}
                allLabel={allLabel}
                testid={`filter-${key}`}
              />
            </div>
          ))}
        </div>
      </details>

      {/* Active filters */}
      <section className="flex flex-wrap gap-2 mb-4" data-testid="active-filters">
        {activeChips.length ? activeChips.map(([l, v]) => {
          const parts = String(v).split(",").map((s) => s.trim()).filter(Boolean);
          const display = parts.length > 2 ? `${parts.length} seleccionadas` : parts.join(", ");
          return <div key={l + v} className="chip active" title={parts.join(", ")}>{l}: {display}</div>;
        }) : <div className="chip active">Vista general — sin filtros</div>}
      </section>

      {/* KPIs (4) — responsive 2/4 columns */}
      <section className="lp2-kpi-grid mb-5" data-testid="kpi-grid">
        <KPI icon={<Users size={18} strokeWidth={2.25}/>} label="Total Leads" value={fmt(total)} note="Volumen filtrado" tip={TIPS.total} accent="#111827" />
        <KPI icon={<CheckCircle2 size={18} strokeWidth={2.25}/>} label="Tasa de Calificación" value={s.qual_rate + "%"} note={`${fmt(s.status.Calificado)} calificados`} tip={TIPS.qual_rate} accent="#16a34a" />
        <KPI icon={<XCircle size={18} strokeWidth={2.25}/>} label="Tasa de Descalificación" value={s.disq_rate + "%"} note={`${fmt(s.status.Descalificado)} descalificados`} tip={TIPS.disq_rate} accent="#dc2626" />
        <KPI icon={<Clock size={18} strokeWidth={2.25}/>} label="Tiempo de Respuesta" value={respFmt(s.avg_response_hours)} note="Creación → modificación" tip={TIPS.response} accent="#f4c400" />
      </section>

      {/* AI Insights */}
      <AIInsightsPanel filters={filters}/>

      {/* Pipeline — 2 cols mobile / 5 cols desktop */}
      <section className="mb-5" data-testid="pipeline-strip">
        <div className="flex items-center mb-3">
          <h3 className="font-display text-base font-extrabold">Pipeline · Razón del Estado</h3>
          <InfoTip>{TIPS.pipeline}</InfoTip>
        </div>
        <div className="crm-pipeline-grid">
          {pipelineCounts.map(({ name, value }) => {
            const pctOfTotal = total ? ((value / total) * 100) : 0;
            return (
              <div
                key={name}
                data-testid={`pipeline-${name}`}
                className="pipeline-step"
                style={{ cursor: "default", padding: "18px 16px" }}
              >
                <div className="pipeline-name">{name}</div>
                <div className="pipeline-value">{fmt(value)}</div>
                <div
                  className="pipeline-pct"
                  data-testid={`pipeline-pct-${name}`}
                  style={{
                    fontSize: 12,
                    fontWeight: 800,
                    marginTop: 4,
                    color: "rgba(17, 24, 39, 0.65)",
                    fontFamily: "var(--font-display, inherit)",
                    letterSpacing: "-0.01em",
                  }}
                  title={`${value.toLocaleString("es-DO")} de ${total.toLocaleString("es-DO")} leads totales`}
                >
                  {pctOfTotal.toFixed(1)}% del total
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Origen de Leads — donut */}
      <OriginDonut origins={s.origins || []} total={total} filters={filters} setF={setF}/>

      {/* BDC First Response Time — KPI operacional del BDC */}
      <div className="mb-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Buscar Journey de un Lead específico
          </div>
          <LeadJourneyLauncher/>
        </div>
      </div>

      {/* Comparison MoM / YoY */}
      {comparison && (
        <section className="surface p-6 mb-5" data-testid="comparison-panel">
          <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
            <div>
              <div className="flex items-center">
                <h2 className="text-xl font-extrabold font-display">Comparativa de Período</h2>
                <InfoTip>{TIPS.comparison}</InfoTip>
              </div>
              <p className="text-sm text-gray-500 font-semibold mt-1">
                {compareMode === "MoM"
                  ? <>Mes a Mes · <strong>{comparison.labels.current_month === "Todos" ? comparison.labels.current_year : `${comparison.labels.current_month} ${comparison.labels.current_year}`}</strong> vs <strong>{comparison.labels.mom_month === "Todos" ? comparison.labels.mom_year : `${comparison.labels.mom_month} ${comparison.labels.mom_year}`}</strong></>
                  : <>Año a Año · <strong>{comparison.labels.current_year}</strong> vs <strong>{comparison.labels.yoy_year || "N/A"}</strong></>
                }
              </p>
            </div>
            <div className="flex gap-2">
              {["MoM", "YoY"].map((m) => (
                <button
                  key={m}
                  data-testid={`compare-mode-${m}`}
                  onClick={() => setCompareMode(m)}
                  className={"chip" + (compareMode === m ? " active" : "")}
                >{m === "MoM" ? "Mes a Mes" : "Año a Año"}</button>
              ))}
            </div>
          </div>
          <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
            <DeltaCard
              label="Total Leads"
              current={comparison.current.total}
              previous={(compareMode === "MoM" ? comparison.previous_mom : (comparison.previous_yoy || {})).total}
              delta={compareMode === "MoM" ? comparison.deltas_mom.total : comparison.deltas_yoy.total}
              higherIsBetter={true}
            />
            <DeltaCard
              label="Tasa Calificación"
              current={comparison.current.qual_rate}
              previous={(compareMode === "MoM" ? comparison.previous_mom : (comparison.previous_yoy || {})).qual_rate}
              delta={compareMode === "MoM" ? comparison.deltas_mom.qual_rate : comparison.deltas_yoy.qual_rate}
              suffix="%"
              isRate={true}
              higherIsBetter={true}
            />
            <DeltaCard
              label="Tasa Descalificación"
              current={comparison.current.disq_rate}
              previous={(compareMode === "MoM" ? comparison.previous_mom : (comparison.previous_yoy || {})).disq_rate}
              delta={compareMode === "MoM" ? comparison.deltas_mom.disq_rate : comparison.deltas_yoy.disq_rate}
              suffix="%"
              isRate={true}
              higherIsBetter={false}
            />
            <DeltaCard
              label="Tiempo Respuesta"
              current={comparison.current.avg_response_hours}
              previous={(compareMode === "MoM" ? comparison.previous_mom : (comparison.previous_yoy || {})).avg_response_hours}
              delta={compareMode === "MoM" ? comparison.deltas_mom.avg_response_hours : comparison.deltas_yoy.avg_response_hours}
              suffix="h"
              higherIsBetter={false}
            />
          </div>

          {/* Dual trend chart */}
          <div>
            <div className="flex justify-between items-baseline mb-2">
              <strong className="text-sm">Tendencia Mensual Comparada</strong>
              <div className="flex gap-3 text-xs font-extrabold text-gray-500">
                <span><i className="sq" style={{ background: "var(--accent)" }}></i>{comparison.labels.current_year}</span>
                {comparison.labels.yoy_year && <span><i className="sq" style={{ background: "#9ca3af" }}></i>{comparison.labels.yoy_year}</span>}
              </div>
            </div>
            <DualTrend
              months={MONTHS}
              current={comparison.trend_current}
              previous={comparison.trend_prev}
              labels={comparison.labels}
              onClickMonth={(m) => toggle("month", m, "Todos")}
              activeMonth={filters.month}
            />
          </div>
        </section>
      )}

      {/* Razones del Estado del Lead — pleno ancho */}
      <section className="surface p-6 mb-5" data-testid="reasons-panel">
        <div className="flex items-center">
          <h2 className="text-xl font-extrabold font-display">Razones del Estado del Lead</h2>
          <InfoTip>{TIPS.reasons}</InfoTip>
        </div>
        <p className="text-sm text-gray-500 font-semibold mt-1 mb-5">Por qué los leads están donde están — el insight más accionable.</p>
        <div className="grid gap-2" style={{ gridTemplateColumns: "1fr 1fr" }}>
          {s.reasons.slice(0, 10).map((r) => {
            const isUnmanaged = ["Nuevo","No Contactado","Intentando Contactar"].includes(r.name);
            const isLost = ["Sin Interés","Sin Presupuesto","Sin Disponibilidad","Data Errónea","Duplicado"].includes(r.name);
            const fillStyle = isUnmanaged
              ? { background: "linear-gradient(90deg,#d97706,#fbbf24)" }
              : isLost
                ? { background: "linear-gradient(90deg,#dc2626,#fca5a5)" }
                : { background: "linear-gradient(90deg,#16a34a,#86efac)" };
            return (
              <div key={r.name} className="row-bar" style={{ cursor: "default" }}>
                <strong className="truncate" title={r.name}>{r.name}</strong>
                <div className="track"><div className="fill" style={{ width: Math.max(6, (r.n / reasonMax) * 100) + "%", ...fillStyle }}></div></div>
                <span className="text-right text-sm font-extrabold text-gray-500">{fmt(r.n)}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Brands + Ranking */}
      <section className="grid gap-4 mb-5" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div className="surface p-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center">
              <h2 className="text-xl font-extrabold font-display">Performance por Marca</h2>
              <InfoTip>{TIPS.brand_perf}</InfoTip>
            </div>
            <div className="inline-flex rounded-lg overflow-hidden border border-gray-200" data-testid="brand-tabs">
              <button
                type="button"
                onClick={() => setBrandTab("nuevos")}
                className={`px-3 py-1.5 text-xs font-extrabold transition-colors ${brandTab === "nuevos" ? "bg-[var(--dark)] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                data-testid="brand-tab-nuevos"
              >Vehículos Nuevos</button>
              <button
                type="button"
                onClick={() => setBrandTab("seminuevos")}
                className={`px-3 py-1.5 text-xs font-extrabold transition-colors ${brandTab === "seminuevos" ? "bg-[var(--dark)] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                data-testid="brand-tab-seminuevos"
              >Seminuevos</button>
            </div>
          </div>
          <p className="text-sm text-gray-500 font-semibold mt-1 mb-5">
            {brandTab === "nuevos"
              ? "Mix de calificación / abierto / descalificado por marca (excluye Seminuevos)."
              : "Mix de calificación / abierto / descalificado para Seminuevos."}
          </p>
          <div className="flex flex-col gap-3" style={{ maxHeight: 480, overflowY: "auto" }}>
            {currentBrands.length === 0 ? (
              <div className="muted-small" data-testid="brand-empty">Sin datos para esta categoría.</div>
            ) : currentBrands.slice(0, 10).map((b) => {
              const t = b.total || 1;
              return (
                <div
                  key={b.name}
                  className={"brand-card" + (filters.brand === b.name ? " active" : "")}
                  onClick={() => toggle("brand", b.name, "Todas")}
                  data-testid={`brand-card-${b.name}`}
                >
                  <div className="flex justify-between items-baseline mb-2">
                    <strong>{b.name}</strong>
                    <span className="text-sm font-extrabold text-gray-500">{fmt(b.total)} leads</span>
                  </div>
                  <div className="stack mb-2">
                    <div className="seg-q" style={{ width: (b.qualified / t) * 100 + "%" }}></div>
                    <div className="seg-o" style={{ width: (b.open / t) * 100 + "%" }}></div>
                    <div className="seg-d" style={{ width: (b.disqualified / t) * 100 + "%" }}></div>
                  </div>
                  <div className="muted-small">Calif {pct(b.qualified, t)} · Abierto {pct(b.open, t)} · Descalif {pct(b.disqualified, t)}</div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 flex-wrap mt-4 text-xs font-extrabold text-gray-500">
            <span><i className="sq" style={{ background: "var(--green)" }}></i>Calificado</span>
            <span><i className="sq" style={{ background: "var(--amber)" }}></i>Abierto</span>
            <span><i className="sq" style={{ background: "var(--red)" }}></i>Descalificado</span>
          </div>
        </div>

        <div className="surface p-6" data-testid="exec-ranking-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center">
              <h2 className="text-xl font-extrabold font-display">Ranking de Ejecutivos</h2>
              <InfoTip>
                <>Cada ejecutivo se ordena por su <strong>Tasa de Calificación</strong> = leads calificados ÷ leads totales. Para <strong>Ventas</strong> se exigen ≥50 leads; para <strong>BDC</strong> ≥30 leads. Verde = ≥20%, amarillo = 12-20%, rojo = &lt;12%.</>
              </InfoTip>
            </div>
            <div className="inline-flex rounded-lg overflow-hidden border border-gray-200" data-testid="exec-tabs">
              <button
                type="button"
                onClick={() => setExecTab("ventas")}
                className={`px-3 py-1.5 text-xs font-extrabold transition-colors ${execTab === "ventas" ? "bg-[var(--dark)] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                data-testid="exec-tab-ventas"
              >Ventas</button>
              <button
                type="button"
                onClick={() => setExecTab("bdc")}
                className={`px-3 py-1.5 text-xs font-extrabold transition-colors ${execTab === "bdc" ? "bg-[var(--dark)] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                data-testid="exec-tab-bdc"
              >BDC</button>
            </div>
          </div>
          <p className="text-sm text-gray-500 font-semibold mt-1 mb-5">
            Top 10 por tasa de calificación (volumen ≥ {currentExecMinVol} leads · {execTab === "ventas" ? "excluye BDC" : "solo Unidad de Negocio BDC"}).
          </p>
          {currentExecs.length ? (
            <div className="flex flex-col gap-3" style={{ maxHeight: 480, overflowY: "auto" }}>
              {currentExecs.map((ex, i) => {
                const active = filters.executive === ex.name;
                const w = Math.max(6, (ex.qual_rate / currentExecMaxQual) * 100);
                const rateClass = ex.qual_rate >= 20 ? "good" : ex.qual_rate >= 12 ? "warn" : "bad";
                return (
                  <div
                    key={ex.name}
                    className={"brand-card" + (active ? " active" : "")}
                    onClick={() => toggle("executive", ex.name, "Todos")}
                    data-testid={`exec-rank-${ex.name}`}
                  >
                    <div className="flex justify-between items-center mb-3 gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="font-display font-extrabold flex-shrink-0"
                          style={{
                            fontSize: i < 3 ? 22 : 14,
                            width: 36, height: 36, borderRadius: 10,
                            display: "grid", placeItems: "center",
                            background: i === 0 ? "var(--accent)" : i === 1 ? "#e5e7eb" : i === 2 ? "#fde68a" : "#f3f4f6",
                            color: "var(--dark)",
                          }}
                        >{i < 3 ? medalFor(i) : `#${i+1}`}</div>
                        <div className="min-w-0">
                          <div className="font-bold text-sm truncate" title={ex.name}>{ex.name}</div>
                          <div className="muted-small">{fmt(ex.total)} leads · {ex.avg_response != null ? respFmt(ex.avg_response) : "—"} resp.</div>
                        </div>
                      </div>
                      <span className={`pill ${rateClass}`} style={{ flexShrink: 0 }}>{ex.qual_rate}% calif</span>
                    </div>
                    <div className="track"><div className="fill" style={{ width: w + "%" }}></div></div>
                  </div>
                );
              })}
            </div>
          ) : <div className="muted-small" data-testid="exec-empty">Sin ejecutivos con volumen suficiente.</div>}
        </div>
      </section>

      {/* Top 5 Campañas por Conversión */}
      {s.top_campaigns_by_conversion && s.top_campaigns_by_conversion.length > 0 && (
        <section className="surface p-6 mb-5" data-testid="top-campaigns-panel">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <div className="flex items-center">
                <h2 className="text-xl font-extrabold font-display">Top 5 Campañas con Mejor Conversión</h2>
                <InfoTip>
                  <>Ranking por <strong>Tasa de Calificación</strong> (calificados ÷ total) con volumen mínimo de <strong>30 leads</strong> para que el ranking sea estadísticamente significativo. Útil para identificar las campañas que entregan leads de mejor calidad — no las que generan más volumen.</>
                </InfoTip>
              </div>
              <p className="text-sm text-gray-500 font-semibold mt-1">
                Calidad sobre volumen — verde ≥20%, amarillo 10-20%, rojo &lt;10%.
              </p>
            </div>
            <span className="text-xs font-extrabold text-gray-400 uppercase tracking-wider">Min. 30 leads</span>
          </div>
          <div className="flex flex-col">
            <div
              className="grid items-center gap-3 px-3 py-2 text-[10px] font-extrabold uppercase tracking-wider"
              style={{ gridTemplateColumns: "40px minmax(0, 1fr) 80px 80px 96px",
                       color: "#94a3b8", borderBottom: "1px solid #e2e8f0" }}
            >
              <span>#</span>
              <span>Campaña</span>
              <span className="text-right">Leads</span>
              <span className="text-right">Calif.</span>
              <span className="text-right">Tasa</span>
            </div>
            {s.top_campaigns_by_conversion.map((c, i) => {
              const rateColor = c.qual_rate >= 20 ? "#16a34a" : c.qual_rate >= 10 ? "#f97316" : "#dc2626";
              const rateBg = c.qual_rate >= 20 ? "#dcfce7" : c.qual_rate >= 10 ? "#fed7aa" : "#fee2e2";
              const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`;
              return (
                <div
                  key={c.name}
                  className="grid items-center gap-3 px-3"
                  data-testid={`top-campaign-${c.name}`}
                  style={{
                    gridTemplateColumns: "40px minmax(0, 1fr) 80px 80px 96px",
                    paddingTop: 12, paddingBottom: 12,
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <div
                    className="font-display font-extrabold"
                    style={{ fontSize: i < 3 ? 20 : 13, lineHeight: 1 }}
                  >{medal}</div>
                  <div className="min-w-0">
                    <div
                      className="font-display font-extrabold truncate"
                      style={{ fontSize: 13, color: "#111827" }}
                      title={c.name}
                    >{c.name}</div>
                  </div>
                  <div
                    className="text-right font-display font-extrabold"
                    style={{ fontSize: 13, color: "#111827", fontVariantNumeric: "tabular-nums" }}
                  >{fmt(c.total)}</div>
                  <div
                    className="text-right font-bold"
                    style={{ fontSize: 12, color: "#64748b", fontVariantNumeric: "tabular-nums" }}
                  >{fmt(c.qualified)}</div>
                  <div className="text-right">
                    <span
                      className="pill font-extrabold"
                      style={{
                        background: rateBg, color: rateColor, fontSize: 12,
                        padding: "3px 10px", letterSpacing: "0.02em",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >{c.qual_rate}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Top modelos (pleno ancho, 2 columnas) */}
      <section className="surface p-6 mb-5" data-testid="models-panel">
        <div className="flex items-center">
          <h2 className="text-xl font-extrabold font-display">Top Modelos de Interés</h2>
          <InfoTip>{TIPS.models}</InfoTip>
        </div>
        <p className="text-sm text-gray-500 font-semibold mt-1 mb-5">Modelos más demandados en el período.</p>
        <div className="grid gap-2" style={{ gridTemplateColumns: "1fr 1fr" }}>
          {s.models.slice(0, 10).map((m) => (
            <div key={m.name} className="row-bar" style={{ cursor: "default" }}>
              <strong className="truncate" title={m.name}>{m.name}</strong>
              <div className="track"><div className="fill" style={{ width: Math.max(6, (m.total / modelMax) * 100) + "%" }}></div></div>
              <span className="text-right text-sm font-extrabold text-gray-500">{fmt(m.total)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Campaign Timeline (heatmap) */}
      {campaignTimeline && campaignTimeline.campaigns.length > 0 && (
        <section className="surface p-6 mb-5" data-testid="campaign-timeline-panel">
          <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
            <div>
              <div className="flex items-center">
          <h2 className="text-xl font-extrabold font-display">Campañas — {campaignTimeline.year}</h2>
                <InfoTip>{TIPS.timeline}</InfoTip>
              </div>
              <p className="text-sm text-gray-500 font-semibold mt-1">
                {timelineMetric === "volume"
                  ? "Volumen de leads por campaña × mes. Color = intensidad."
                  : "Tasa de calificación por campaña × mes. Color = % calidad."}
              </p>
            </div>
            <div className="flex gap-2">
              {[["volume","Volumen"], ["quality","Calidad %"]].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`timeline-metric-${v}`}
                  onClick={() => setTimelineMetric(v)}
                  className={"chip" + (timelineMetric === v ? " active" : "")}
                >{l}</button>
              ))}
            </div>
          </div>
          <CampaignTimeline
            data={campaignTimeline}
            metric={timelineMetric}
            activeMonth={filters.month}
            onClickMonth={(m) => toggle("month", m, "Todos")}
          />
        </section>
      )}

      {/* Ubicación del Lead — pleno ancho */}
      <section className="surface p-6 mb-5" data-testid="branches-panel">
        <div className="flex items-center">
          <h2 className="text-xl font-extrabold font-display">Ubicación del Lead</h2>
          <InfoTip>{TIPS.branches}</InfoTip>
        </div>
        <p className="text-sm text-gray-500 font-semibold mt-1 mb-5">Volumen y calidad por Unidad de Negocio asignada. Click para filtrar.</p>
        <div className="grid gap-2" style={{ gridTemplateColumns: "1fr 1fr" }}>
          {s.branches.slice(0, 14).map((b) => {
            const qr = b.total ? ((b.qualified / b.total) * 100).toFixed(1) : "0.0";
            const isDealers = dealersBreakdown && /red\s*de\s*dealers/i.test(b.name);
            return (
              <BranchBarRow
                key={b.name}
                branch={b}
                qr={qr}
                topTotal={s.branches[0]?.total || 1}
                active={filters.branch === b.name}
                onClick={() => toggle("branch", b.name, "Todas")}
                dealersData={isDealers ? dealersBreakdown : null}
              />
            );
          })}
        </div>
      </section>

      <TradeInPotentialSection filters={{ year: filters.year, month: filters.month, brand: filters.brand, branch: filters.branch,
                                          executive: filters.executive, campaign: filters.campaign }}/>

      {/* Unmanaged paginated */}
      <section className="surface p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="flex items-center gap-1">
              <h2 className="text-base font-extrabold font-display">Leads Sin Gestionar</h2>
              <InfoTip>{TIPS.unmanaged}</InfoTip>
            </div>
            <p className="text-xs text-gray-500 font-semibold mt-0.5">
              Estado <span className="font-bold">Nuevo · No Contactado · Intentando Contactar</span> · paginado y exportable.
            </p>
          </div>
        </div>
        <UnmanagedPaginated filters={filters}/>
      </section>

      {/* AI Chat floating widget */}
      <AIChatWidget filters={filters}/>
    </div>
  );
}

function SyncStatusBadge() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/integrations/powerbi/last-sync")
      .then((r) => { if (alive) setInfo(r.data); })
      .catch(() => { if (alive) setInfo({ configured: false }); });
    load();
    const t = setInterval(load, 60000); // refresh once per minute
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!info) return null;
  if (!info.configured) {
    return (
      <Link
        to="/admin/integrations"
        className="pill flex items-center gap-1.5"
        style={{ background: "#f3f4f6", color: "#475569", fontSize: 11, padding: "3px 10px", fontWeight: 700 }}
        title="Power BI sync no configurado — click para ir al admin"
        data-testid="sync-badge"
      >
        <span className="rounded-full" style={{ width: 6, height: 6, background: "#cbd5e1" }}/>
        Power BI sync · No configurado
      </Link>
    );
  }
  const last = info.last_sync_at ? new Date(info.last_sync_at) : null;
  if (!last) {
    return (
      <span className="pill flex items-center gap-1.5" style={{ background: "#fffbeb", color: "#92400e", fontSize: 11, padding: "3px 10px", fontWeight: 700 }} data-testid="sync-badge">
        <span className="rounded-full" style={{ width: 6, height: 6, background: "#f97316" }}/>
        Esperando primera sincronización
      </span>
    );
  }
  const minsAgo = Math.floor((Date.now() - last.getTime()) / 60000);
  const isStale = minsAgo > 240; // > 4h = rojo
  const isWarn = minsAgo > 90;   // > 1.5h = naranja
  const color = isStale ? "#dc2626" : isWarn ? "#f97316" : "#16a34a";
  const bg    = isStale ? "#fee2e2" : isWarn ? "#fff7ed" : "#dcfce7";
  const label = minsAgo < 1 ? "ahora" : minsAgo < 60 ? `${minsAgo}m` : minsAgo < 1440 ? `${Math.floor(minsAgo/60)}h` : `${Math.floor(minsAgo/1440)}d`;
  return (
    <Link
      to="/admin/integrations"
      className="pill flex items-center gap-1.5"
      style={{ background: bg, color, fontSize: 11, padding: "3px 10px", fontWeight: 700 }}
      title={`Power BI · ${info.inserted || 0} insertados · ${info.updated || 0} actualizados · ${last.toLocaleString("es-DO")}`}
      data-testid="sync-badge"
    >
      <span className="rounded-full" style={{ width: 6, height: 6, background: color }}/>
      Power BI · Sincronizado hace {label}
    </Link>
  );
}

function KPI({ icon, label, value, note, tip, accent = "#111827" }) {
  return (
    <div className="kpi-card surface relative" data-testid={`kpi-${label}`}>
      <span className="kpi-accent" style={{ background: accent }} aria-hidden="true"></span>
      <div className="relative p-4 md:p-5">
        <div className="flex items-start justify-between mb-3 md:mb-4">
          <div className="kpi-icon-clean" style={{ color: accent, boxShadow: `inset 0 0 0 1px ${accent}1f` }}>{icon}</div>
          {tip && <InfoTip>{tip}</InfoTip>}
        </div>
        <div className="label-up mb-2">{label}</div>
        <div className="kpi-value font-display font-extrabold leading-none mb-2" title={value} style={{ letterSpacing: "-0.02em" }}>{value}</div>
        <div className="text-xs font-semibold text-gray-500 truncate" title={note}>{note}</div>
      </div>
    </div>
  );
}

const ORIGIN_COLORS = ["#f4c400", "#111827", "#16a34a", "#3b82f6", "#dc2626", "#a855f7", "#f97316", "#0891b2", "#8b929d", "#84cc16", "#ec4899", "#64748b"];

function OriginDonut({ origins, total, filters, setF }) {
  const [sortBy, setSortBy] = useState("volume"); // volume | quality
  const [showAll, setShowAll] = useState(false);

  const all = (origins || []).map((o, i) => ({
    name: o.name,
    total: o.total,
    qualified: o.qualified || 0,
    qual_rate: o.total ? +((o.qualified / o.total) * 100).toFixed(1) : 0,
    color: ORIGIN_COLORS[i % ORIGIN_COLORS.length],
    top_contact: o.top_contact || null,
    top_contact_share: o.top_contact_share ?? null,
    contact_breakdown: o.contact_breakdown || [],
  }));
  const totalSum = all.reduce((a, o) => a + o.total, 0) || 1;
  const totalQualified = all.reduce((a, o) => a + o.qualified, 0);
  const overallRate = totalSum ? +((totalQualified / totalSum) * 100).toFixed(1) : 0;

  // Insight badges
  const topVolume = all.length ? all.reduce((a, b) => (b.total > a.total ? b : a)) : null;
  // "Mejor calidad" needs both scale (≥500 leads) AND a quality rate that
  // actually beats the global average — otherwise a single high-volume
  // channel with low % (e.g. Redes Sociales 2.9% vs global 13.2%) wins
  // the badge by default and misleads the user.
  const eligibleForQuality = all.filter((o) => o.total >= 500 && o.qual_rate > overallRate);
  const bestQuality = eligibleForQuality.length
    ? eligibleForQuality.reduce((a, b) => (b.qual_rate > a.qual_rate ? b : a)) : null;
  const worstQuality = all.filter((o) => o.total >= 500 && o.qual_rate < 5);

  // Sort + collapse
  const sorted = [...all].sort((a, b) => sortBy === "volume" ? b.total - a.total : b.qual_rate - a.qual_rate);
  const TOP_N = 6;
  const pinned = new Set();
  if (topVolume) pinned.add(topVolume.name);
  if (bestQuality) pinned.add(bestQuality.name);
  worstQuality.forEach((o) => pinned.add(o.name));
  const topItems = sorted.slice(0, TOP_N);
  const pinnedExtras = sorted.filter((o) => pinned.has(o.name) && !topItems.some((t) => t.name === o.name));
  const visible = showAll ? sorted : [...topItems, ...pinnedExtras];
  const rest = showAll ? [] : sorted.filter((o) => !visible.some((v) => v.name === o.name));
  const restAgg = rest.length ? {
    name: `Otras ${rest.length} fuentes`,
    total: rest.reduce((a, o) => a + o.total, 0),
    qualified: rest.reduce((a, o) => a + o.qualified, 0),
    color: "#cbd5e1",
    isGroup: true,
  } : null;
  if (restAgg) restAgg.qual_rate = restAgg.total ? +((restAgg.qualified / restAgg.total) * 100).toFixed(1) : 0;

  // SVG donut by volume with labels for top 3
  const byVol = [...all].sort((a, b) => b.total - a.total);
  const R_OUTER = 78;
  const R_INNER = 50;
  const CX = 90, CY = 90;
  let cursor = -Math.PI / 2; // start at top
  const segments = byVol.map((o) => {
    const frac = o.total / totalSum;
    const angle = frac * 2 * Math.PI;
    const start = cursor;
    const end = cursor + angle;
    cursor = end;
    const large = angle > Math.PI ? 1 : 0;
    const x1 = CX + R_OUTER * Math.cos(start), y1 = CY + R_OUTER * Math.sin(start);
    const x2 = CX + R_OUTER * Math.cos(end),   y2 = CY + R_OUTER * Math.sin(end);
    const x3 = CX + R_INNER * Math.cos(end),   y3 = CY + R_INNER * Math.sin(end);
    const x4 = CX + R_INNER * Math.cos(start), y4 = CY + R_INNER * Math.sin(start);
    const d = `M ${x1} ${y1} A ${R_OUTER} ${R_OUTER} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${R_INNER} ${R_INNER} 0 ${large} 0 ${x4} ${y4} Z`;
    const mid = (start + end) / 2;
    const labelR = (R_OUTER + R_INNER) / 2;
    const lx = CX + labelR * Math.cos(mid);
    const ly = CY + labelR * Math.sin(mid);
    return { ...o, d, frac, mid, lx, ly };
  });
  const top3Names = new Set(byVol.slice(0, 3).map((o) => o.name));

  const active = filters.origin && filters.origin !== "Todos" ? filters.origin : null;
  const selectedRow = active ? all.find((o) => o.name === active) : null;
  const onClickRow = (name) => setF("origin", active === name ? "Todos" : name);

  if (!all.length) return null;
  const fmtN = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
  const centerVal = selectedRow ? selectedRow.total : totalSum;
  const centerRate = selectedRow ? selectedRow.qual_rate : overallRate;
  const centerRateColor = centerRate >= 15 ? "#16a34a" : centerRate >= 5 ? "#f97316" : "#dc2626";

  return (
    <section className="surface mb-5" data-testid="origin-donut-panel" style={{ padding: 0, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9" }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center">
              <h3 className="font-display text-base font-extrabold" style={{ letterSpacing: "-0.01em" }}>
                Origen de Leads · Canal de Adquisición
              </h3>
              <InfoTip>Distribución del campo <strong>Origen del cliente potencial</strong>. La barra muestra el <strong>volumen relativo</strong> y la porción verde los <strong>calificados</strong>. Click en una fila filtra el dashboard.</InfoTip>
            </div>
            <p className="text-sm text-gray-500 font-semibold mt-1">
              {active
                ? <>Filtrado por <strong style={{ color: "#111827" }}>{active}</strong> · {fmtN(selectedRow?.total || 0)} leads · {selectedRow?.qual_rate}% calificación</>
                : <>{all.length} fuentes distintas · {fmtN(totalQualified)} calificados de {fmtN(totalSum)} ({overallRate}% global)</>
              }
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSortBy((s) => s === "volume" ? "quality" : "volume")}
            className="btn flex items-center gap-2"
            style={{ fontSize: 12, padding: "6px 12px" }}
            data-testid="origin-sort-toggle"
            title="Cambiar ordenamiento"
          >
            <ArrowUpDown size={12}/>
            Ordenar: <strong>{sortBy === "volume" ? "Volumen" : "Calidad"}</strong>
          </button>
        </div>
      </div>

      {/* Body: SVG donut + compact table */}
      <div className="grid gap-6" style={{ gridTemplateColumns: "minmax(200px, 240px) 1fr", padding: "20px 24px", alignItems: "start" }}>
        {/* SVG Donut with labels */}
        <div className="flex flex-col items-center">
          <div className="relative" style={{ width: 180, height: 180 }}>
            <svg viewBox="0 0 180 180" width="180" height="180" style={{ overflow: "visible" }}>
              {segments.map((seg) => {
                const dim = active && active !== seg.name;
                return (
                  <path
                    key={seg.name}
                    d={seg.d}
                    fill={seg.color}
                    opacity={dim ? 0.25 : (active === seg.name ? 1 : 0.95)}
                    style={{ cursor: "pointer", transition: "opacity 0.2s" }}
                    onClick={() => onClickRow(seg.name)}
                  >
                    <title>{seg.name}: {fmtN(seg.total)} ({(seg.frac * 100).toFixed(1)}%)</title>
                  </path>
                );
              })}
              {/* % labels on top 3 slices only (avoids clutter) */}
              {segments.filter((s) => top3Names.has(s.name) && s.frac >= 0.04).map((seg) => (
                <text
                  key={`lbl-${seg.name}`}
                  x={seg.lx}
                  y={seg.ly}
                  textAnchor="middle"
                  dominantBaseline="central"
                  style={{ fontSize: 11, fontWeight: 800, fill: seg.color === "#f4c400" ? "#78350f" : "#fff", pointerEvents: "none", fontFamily: "var(--font-display, inherit)" }}
                >
                  {(seg.frac * 100).toFixed(0)}%
                </text>
              ))}
            </svg>
            {/* Center label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="font-display font-extrabold leading-none" style={{ fontSize: 22, letterSpacing: "-0.03em", color: "#111827" }}>
                {fmtN(centerVal)}
              </div>
              <div className="text-xs font-semibold mt-1 truncate px-2" style={{ color: "#64748b", maxWidth: 120 }} title={active || "Total leads"}>
                {active ? active.length > 14 ? active.slice(0, 12) + "…" : active : "Total leads"}
              </div>
              <div className="text-xs font-extrabold mt-1.5" style={{ color: centerRateColor }}>
                {centerRate}% calif
              </div>
            </div>
          </div>
          <div className="mt-3 text-center" style={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.5, fontWeight: 600 }}>
            Top 3 fuentes muestran su % directo
          </div>
        </div>

        {/* Compact table */}
        <div className="flex flex-col" data-testid="origin-bars-list">
          {/* Header row */}
          <div
            className="grid items-center gap-3 px-3 py-2 text-[10px] font-extrabold uppercase tracking-wider"
            style={{
              gridTemplateColumns: "minmax(0, 1fr) 130px 110px 64px 64px 64px",
              color: "#94a3b8",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <span>Canal</span>
            <span>Vía 1er contacto</span>
            <span className="text-center">Distribución</span>
            <span className="text-right">Leads</span>
            <span className="text-right">Share</span>
            <span className="text-right">Tasa</span>
          </div>

          {visible.map((o) => {
            const isActive = active === o.name;
            const isDimmed = active && active !== o.name;
            const isTopVol = topVolume && topVolume.name === o.name && sortBy === "volume";
            const isBestQual = bestQuality && bestQuality.name === o.name;
            const isWorst = worstQuality.some((w) => w.name === o.name);
            const volPct = (o.total / totalSum) * 100;
            const qualPct = o.total ? (o.qualified / o.total) * 100 : 0;
            const qualColor = o.qual_rate >= 15 ? "#16a34a" : o.qual_rate >= 5 ? "#f97316" : "#dc2626";
            // Prefer best/worst quality badge over top volume to surface insight
            let badge = null;
            if (isBestQual) badge = { icon: <Trophy size={9}/>, text: "MEJOR CALIDAD", bg: "#dcfce7", color: "#166534" };
            else if (isWorst) badge = { icon: <AlertTriangle size={9}/>, text: "BAJA CALIDAD", bg: "#fee2e2", color: "#991b1b" };
            else if (isTopVol) badge = { icon: <TrendingUp size={9}/>, text: "TOP VOLUMEN", bg: "#fef3c7", color: "#92400e" };

            return (
              <button
                key={o.name}
                type="button"
                onClick={() => onClickRow(o.name)}
                className="origin-row grid items-center gap-3 px-3"
                data-testid={`origin-row-${o.name}`}
                style={{
                  gridTemplateColumns: "minmax(0, 1fr) 130px 110px 64px 64px 64px",
                  textAlign: "left",
                  paddingTop: 10,
                  paddingBottom: 10,
                  borderBottom: "1px solid #f1f5f9",
                  background: isActive ? "#fffbeb" : "transparent",
                  opacity: isDimmed ? 0.45 : 1,
                  transition: "opacity 0.2s, background 0.2s",
                  cursor: "pointer",
                  borderLeft: isActive ? "2px solid #f4c400" : "2px solid transparent",
                }}
              >
                {/* Channel col: dot + name + badge */}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="rounded-full flex-shrink-0" style={{ background: o.color, width: 9, height: 9 }}/>
                  <span className="font-display font-extrabold truncate" style={{ fontSize: 13, color: "#111827" }} title={o.name}>{o.name}</span>
                  {badge && (
                    <span
                      className="pill flex items-center gap-1 flex-shrink-0"
                      style={{ background: badge.bg, color: badge.color, fontSize: 8.5, fontWeight: 800, padding: "1px 6px", letterSpacing: "0.03em" }}
                      title={badge.text}
                    >
                      {badge.icon} {badge.text}
                    </span>
                  )}
                </div>

                {/* Vía de 1er contacto · dominant + optional top-3 tooltip */}
                <div
                  className="flex flex-col min-w-0"
                  title={
                    o.contact_breakdown && o.contact_breakdown.length > 1
                      ? o.contact_breakdown.map((c) => `${c.name}: ${fmtN(c.count)} (${c.share}%)`).join("\n")
                      : undefined
                  }
                >
                  <span
                    className="font-display font-extrabold truncate"
                    style={{ fontSize: 12, color: "#334155" }}
                  >
                    {o.top_contact || "—"}
                  </span>
                  {o.top_contact_share != null && o.top_contact_share < 100 && (
                    <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700 }}>
                      {o.top_contact_share}% del canal
                    </span>
                  )}
                </div>

                {/* Distribution mini-bar */}
                <div className="relative" style={{ height: 8, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    position: "absolute", left: 0, top: 0, bottom: 0,
                    width: `${Math.max(2, volPct)}%`,
                    background: `${o.color}33`,
                    borderRadius: 4,
                  }}/>
                  <div style={{
                    position: "absolute", left: 0, top: 0, bottom: 0,
                    width: `${(volPct * qualPct) / 100}%`,
                    background: qualColor,
                    borderRadius: 4,
                    transition: "width 0.3s ease",
                  }}/>
                </div>

                {/* Leads */}
                <div className="text-right font-display font-extrabold" style={{ fontSize: 13, color: "#111827" }}>
                  {fmtN(o.total)}
                </div>

                {/* Share */}
                <div className="text-right font-bold" style={{ fontSize: 12, color: "#64748b" }}>
                  {volPct.toFixed(1)}%
                </div>

                {/* Tasa */}
                <div className="text-right">
                  <div className="font-display font-extrabold" style={{ fontSize: 13, color: qualColor }}>
                    {o.qual_rate}%
                  </div>
                  <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>
                    {fmtN(o.qualified)} calif
                  </div>
                </div>
              </button>
            );
          })}

          {/* "Otros" aggregated row when collapsed */}
          {restAgg && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="origin-row grid items-center gap-3 px-3"
              data-testid="origin-show-more"
              style={{
                gridTemplateColumns: "minmax(0, 1fr) 130px 110px 64px 64px 64px",
                paddingTop: 10, paddingBottom: 10,
                background: "#f8fafc",
                cursor: "pointer", textAlign: "left",
                borderTop: "1px dashed #cbd5e1",
                borderLeft: "2px solid transparent",
              }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="rounded-full flex-shrink-0" style={{ background: restAgg.color, width: 9, height: 9 }}/>
                <span className="font-display font-extrabold truncate" style={{ fontSize: 13, color: "#475569" }}>{restAgg.name}</span>
                <ChevronDown size={12} style={{ color: "#64748b", flexShrink: 0 }}/>
              </div>
              {/* Placeholder for Vía 1er contacto · aggregated row doesn't have a dominant value */}
              <div style={{ fontSize: 11, color: "#cbd5e1", fontWeight: 700 }}>—</div>
              <div className="relative" style={{ height: 8, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.max(2, (restAgg.total / totalSum) * 100)}%`, background: "#cbd5e133", borderRadius: 4 }}/>
              </div>
              <div className="text-right font-display font-extrabold" style={{ fontSize: 13, color: "#475569" }}>{fmtN(restAgg.total)}</div>
              <div className="text-right font-bold" style={{ fontSize: 12, color: "#94a3b8" }}>{((restAgg.total / totalSum) * 100).toFixed(1)}%</div>
              <div className="text-right font-display font-extrabold" style={{ fontSize: 13, color: "#64748b" }}>{restAgg.qual_rate}%</div>
            </button>
          )}
          {showAll && all.length > TOP_N && (
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className="flex items-center justify-center gap-1 mt-2 mx-auto text-xs font-bold"
              style={{ color: "#64748b", padding: "6px 12px", background: "transparent", border: "none", cursor: "pointer" }}
              data-testid="origin-show-less"
            >
              <ChevronUp size={12}/> Ver menos
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function InfoTip({ children }) {
  return (
    <span className="info-tip" tabIndex={0} role="tooltip" data-testid="info-tip">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
      <span className="info-tip-content">{children}</span>
    </span>
  );
}

function BigStat({ label, sub, value, pctVal, width, color }) {
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1">
        <div>
          <strong>{label}</strong>
          <span className="ml-2 text-xs text-gray-500 font-semibold">{sub}</span>
        </div>
        <div className="text-right">
          <div className="font-display font-extrabold text-lg leading-none">{value}</div>
          <div className="muted-small">{pctVal}</div>
        </div>
      </div>
      <div className="track"><div className="fill" style={{ width: Math.max(6, width) + "%", background: color }}></div></div>
    </div>
  );
}

function DeltaCard({ label, current, previous, delta, suffix = "", isRate = false, higherIsBetter = true }) {
  const cv = current == null ? "—" : `${fmt2(current, isRate)}${suffix}`;
  const pv = previous == null ? "—" : `${fmt2(previous, isRate)}${suffix}`;
  const pct = delta?.pct;
  const abs = delta?.abs;
  const isUp = (pct ?? abs ?? 0) > 0;
  const isDown = (pct ?? abs ?? 0) < 0;
  const isGood = (higherIsBetter && isUp) || (!higherIsBetter && isDown);
  const color = pct === null || pct === undefined ? "#6b7280" : (isGood ? "#16a34a" : isDown || isUp ? "#dc2626" : "#6b7280");
  const arrow = isUp ? "▲" : isDown ? "▼" : "—";
  return (
    <div className="surface p-4" style={{ borderTop: `3px solid ${color}` }} data-testid={`delta-${label}`}>
      <div className="label-up mb-2 truncate" title={label}>{label}</div>
      <div className="flex justify-between items-baseline mb-2 gap-2">
        <div className="font-display text-2xl font-extrabold leading-none truncate">{cv}</div>
        <div className="text-xs font-bold" style={{ color, whiteSpace: "nowrap" }}>
          {arrow} {pct == null
            ? (abs != null ? (isRate ? `${abs > 0 ? "+" : ""}${abs}${suffix} pts` : `${abs > 0 ? "+" : ""}${fmt(abs)}`) : "n/a")
            : `${pct > 0 ? "+" : ""}${pct}%`}
        </div>
      </div>
      <div className="muted-small">Anterior: <strong className="text-gray-700">{pv}</strong></div>
    </div>
  );
}

function fmt2(v, isRate) {
  if (v == null) return "—";
  if (isRate) return v.toFixed(1);
  return Number(v).toLocaleString("es-DO");
}

function DualTrend({ months, current, previous, labels, onClickMonth, activeMonth }) {
  const all = [...months.map(m => current[m] || 0), ...months.map(m => previous[m] || 0)];
  const max = Math.max(...all, 1);
  return (
    <div className="grid gap-2 pt-3 border-t border-gray-200" style={{ gridTemplateColumns: `repeat(${months.length},1fr)`, minHeight: 200 }} data-testid="dual-trend-chart">
      {months.map((m) => {
        const c = current[m] || 0;
        const p = previous[m] || 0;
        const ch = (c / max) * 100;
        const ph = (p / max) * 100;
        const active = activeMonth === m;
        return (
          <div
            key={m}
            className={"flex flex-col items-center gap-1 cursor-pointer rounded-lg p-1 transition" + (active ? " bg-yellow-50 ring-2 ring-yellow-400" : "")}
            onClick={() => onClickMonth(m)}
            data-testid={`dual-trend-month-${m}`}
            style={{ minHeight: 180 }}
          >
            <div className="text-[10px] font-extrabold text-gray-700">{fmt(c)}</div>
            <div className="flex items-end justify-center gap-1" style={{ width: "100%", height: 130 }}>
              <div title={`${labels.current_year}: ${fmt(c)}`} style={{ width: 12, height: c ? Math.max(4, ch) + "%" : 0, background: "linear-gradient(180deg,#111827,#f4c400)", borderRadius: "4px 4px 0 0" }}></div>
              {labels.yoy_year && (
                <div title={`${labels.yoy_year}: ${fmt(p)}`} style={{ width: 12, height: p ? Math.max(4, ph) + "%" : 0, background: "#9ca3af", borderRadius: "4px 4px 0 0", opacity: 0.7 }}></div>
              )}
            </div>
            <div className="text-[10px] font-extrabold text-gray-500">{m}</div>
          </div>
        );
      })}
    </div>
  );
}



function CampaignTimeline({ data, metric, activeMonth, onClickMonth }) {
  const { campaigns, months, max_cell } = data;
  // For quality metric, scale 0-100 (cap at 50 for vivid colors)
  const cellBg = (cell) => {
    if (metric === "volume") {
      const intensity = max_cell ? cell.n / max_cell : 0;
      if (intensity === 0) return "#f9fafb";
      // dark → accent gradient
      const alpha = Math.min(0.15 + intensity * 0.85, 1);
      return `rgba(244, 196, 0, ${alpha})`;
    }
    // quality
    if (cell.n === 0) return "#f9fafb";
    const q = cell.qual_rate; // 0-100
    if (q >= 30) return "rgba(22,163,74,0.85)";    // green strong
    if (q >= 18) return "rgba(22,163,74,0.55)";    // green mid
    if (q >= 10) return "rgba(217,119,6,0.7)";     // amber
    if (q >= 3) return "rgba(220,38,38,0.55)";     // red mid
    return "rgba(220,38,38,0.85)";                  // red strong
  };
  const cellText = (cell) => {
    if (cell.n === 0) return "";
    if (metric === "volume") return cell.n >= 1000 ? `${(cell.n/1000).toFixed(1)}k` : String(cell.n);
    return `${cell.qual_rate}%`;
  };
  const cellTextColor = (cell) => {
    if (metric === "volume") {
      const intensity = max_cell ? cell.n / max_cell : 0;
      return intensity > 0.55 ? "#111827" : "#374151";
    }
    if (cell.n === 0) return "transparent";
    return cell.qual_rate >= 18 || cell.qual_rate < 3 ? "white" : "#111827";
  };

  return (
    <div style={{ overflowX: "auto" }} data-testid="campaign-timeline-chart">
      <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 280px) repeat(12, minmax(56px, 1fr)) minmax(80px, 100px)", gap: 4, minWidth: 980 }}>
        {/* Header */}
        <div></div>
        {months.map((m) => (
          <div
            key={m}
            onClick={() => onClickMonth(m)}
            className="text-xs font-extrabold text-center cursor-pointer rounded-md py-1.5 transition"
            style={{
              background: activeMonth === m ? "var(--accent)" : "transparent",
              color: activeMonth === m ? "#111827" : "#6b7280",
            }}
            data-testid={`timeline-col-${m}`}
          >{m}</div>
        ))}
        <div className="text-xs font-extrabold text-right text-gray-500 pr-2 self-center">TOTAL</div>

        {/* Rows */}
        {campaigns.map((c) => (
          <Fragment key={c.name}>
            <div className="text-xs font-bold pr-2 self-center truncate" title={c.name}>{c.name}</div>
            {c.monthly.map((cell) => (
              <div
                key={cell.month}
                data-testid={`timeline-cell-${c.name}-${cell.month}`}
                onClick={() => cell.n > 0 && onClickMonth(cell.month)}
                title={`${c.name} · ${cell.month}: ${cell.n} leads · ${cell.qual_rate}% calif`}
                className="text-center text-[11px] font-extrabold rounded-md py-2.5 transition"
                style={{
                  background: cellBg(cell),
                  color: cellTextColor(cell),
                  cursor: cell.n > 0 ? "pointer" : "default",
                  border: activeMonth === cell.month ? "1.5px solid #f4c400" : "1px solid #f3f4f6",
                  minHeight: 38,
                }}
              >{cellText(cell)}</div>
            ))}
            <div className="text-right pr-2 self-center">
              <div className="font-extrabold text-sm">{fmt(c.total)}</div>
              <div className="text-[10px] font-bold text-green-700">{c.qual_rate}% c</div>
            </div>
          </Fragment>
        ))}
      </div>

      {/* Legend */}
      <div className="flex justify-end items-center gap-3 mt-4 text-[11px] font-extrabold text-gray-500">
        {metric === "volume" ? (
          <>
            <span>Menos</span>
            <div className="flex gap-1">
              {[0.15, 0.35, 0.55, 0.75, 1].map((a) => (
                <div key={a} style={{ width: 18, height: 14, borderRadius: 3, background: `rgba(244,196,0,${a})` }}></div>
              ))}
            </div>
            <span>Más</span>
          </>
        ) : (
          <>
            <span style={{ background: "rgba(220,38,38,0.85)", padding: "2px 6px", borderRadius: 4, color: "white" }}>&lt;3%</span>
            <span style={{ background: "rgba(220,38,38,0.55)", padding: "2px 6px", borderRadius: 4, color: "white" }}>3-10%</span>
            <span style={{ background: "rgba(217,119,6,0.7)", padding: "2px 6px", borderRadius: 4 }}>10-18%</span>
            <span style={{ background: "rgba(22,163,74,0.55)", padding: "2px 6px", borderRadius: 4, color: "white" }}>18-30%</span>
            <span style={{ background: "rgba(22,163,74,0.85)", padding: "2px 6px", borderRadius: 4, color: "white" }}>≥30%</span>
          </>
        )}
      </div>
    </div>
  );
}


function BranchBarRow({ branch, qr, topTotal, active, onClick, dealersData }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div
        className={"row-bar" + (active ? " active" : "")}
        onClick={onClick}
        data-testid={`branch-row-${branch.name}`}
      >
        <strong className="truncate flex items-center gap-1" title={branch.name}>
          {branch.name}
          {dealersData && <Info size={11} style={{ opacity: 0.5, flexShrink: 0 }}/>}
        </strong>
        <div className="track"><div className="fill" style={{ width: Math.max(6, (branch.total / topTotal) * 100) + "%" }}/></div>
        <span className="text-right text-xs font-extrabold text-gray-500">
          {fmt(branch.total)}<br/><span className="text-green-700">{qr}% c</span>
        </span>
      </div>
      {dealersData && hover && (
        <div
          className="absolute z-20 surface p-3"
          style={{
            top: "calc(100% + 4px)", left: 0, right: "auto",
            minWidth: 280, background: "#0f172a", color: "#fff",
            border: "1px solid #1e293b", boxShadow: "0 10px 25px rgba(0,0,0,0.25)", borderRadius: 10,
          }}
          data-testid="dealers-tooltip"
        >
          <div className="text-xs font-bold mb-2" style={{ color: "#fde047" }}>
            {dealersData.bucket || "Red de Dealers Externos"} · {fmt(dealersData.total)} leads
          </div>
          <div className="space-y-1">
            {(dealersData.dealers || []).map((dl) => {
              const pct = ((dl.count / (dealersData.total || 1)) * 100).toFixed(0);
              return (
                <div key={dl.dealer_name} className="flex items-center gap-2 text-xs">
                  <div className="flex-1 truncate" style={{ color: "#e2e8f0" }}>{dl.dealer_name}</div>
                  <div style={{ width: 60, height: 4, background: "#1e293b", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: "#fde047" }}/>
                  </div>
                  <div className="font-bold" style={{ width: 30, textAlign: "right" }}>{dl.count}</div>
                  <div className="text-xs" style={{ width: 30, textAlign: "right", color: "#94a3b8" }}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
